import {
  MultiMediaResponse,
} from 'dtos';

export type InsuranceCardDragItem = {
  dragId: string;
  patientId: number;
  mediaId: number;
  fileName: string;
  previewMedia: MultiMediaResponse;
  sourceType: 'insurance' | 'unassociated';
  sourceInsuranceId?: number;
  insuranceAttachmentId?: number;
};

export type DropTarget =
  | { type: 'insurance'; insuranceId: number }
  | { type: 'unassociated' };

export type BusyContainerState = {
  sourceKey: string;
  destinationKey: string;
};

export type RefetchPlan = {
  insuranceIds: number[];
  shouldRefetchUnassociated: boolean;
};

export const unassociatedDroppableId = 'unassociated';

export function getInsuranceDroppableId(insuranceId: number) {
  return `insurance:${insuranceId}`;
}

export function getInsuranceCardDragId(
  insuranceId: number,
  mediaId: number,
) {
  return `insurance-media:${insuranceId}:${mediaId}`;
}

export function getUnassociatedInsuranceCardDragId(mediaId: number) {
  return `unassociated-media:${mediaId}`;
}

export function parseDroppableId(droppableId: string): DropTarget {
  const isUnassociatedContainer = droppableId === unassociatedDroppableId;
  let target: DropTarget = { type: 'unassociated' };

  if (!isUnassociatedContainer) {
    const [, insuranceId] = droppableId.split(':');
    target = {
      type: 'insurance',
      insuranceId: Number(insuranceId),
    };
  }

  return target;
}

export function getInsuranceCardMoveKind(
  item: InsuranceCardDragItem,
  destination: DropTarget,
) {
  let moveKind = 'noop';

  if (item.sourceType === 'insurance' && destination.type === 'insurance') {
    moveKind = item.sourceInsuranceId === destination.insuranceId
      ? 'noop'
      : 'insurance-to-insurance';
  } else if (item.sourceType === 'insurance' && destination.type === 'unassociated') {
    moveKind = 'insurance-to-unassociated';
  } else if (item.sourceType === 'unassociated' && destination.type === 'insurance') {
    moveKind = 'unassociated-to-insurance';
  }

  return moveKind;
}

export function getInsuranceCardRefetchPlan(
  item: InsuranceCardDragItem,
  destination: DropTarget,
  options?: {
    shouldRefetchUnassociated?: boolean;
  },
): RefetchPlan {
  const insuranceIds = new Set<number>();
  const moveKind = getInsuranceCardMoveKind(item, destination);
  const shouldRefetchUnassociated = !!options?.shouldRefetchUnassociated
    || item.sourceType === 'unassociated'
    || destination.type === 'unassociated';

  if (item.sourceType === 'insurance' && (item.sourceInsuranceId ?? 0) > 0) {
    insuranceIds.add(item.sourceInsuranceId ?? 0);
  }

  if (destination.type === 'insurance' && moveKind !== 'noop') {
    insuranceIds.add(destination.insuranceId);
  }

  return {
    insuranceIds: Array.from(insuranceIds),
    shouldRefetchUnassociated,
  };
}
